import { type ReactNode, useCallback, useState } from 'react';
import { shouldLoadGalleryPaginatorSlide } from './lib/shouldLoadGalleryPaginatorSlide';
import { GalleryWithPaginator, type GalleryWithPaginatorProps } from './GalleryWithPaginator';

type ItemProps = {
    id: string;
    /** Индекс (порядковый номер) слайда */
    index: number;
};

export type GalleryWithPaginatorLazyProps<ItemPropsT extends ItemProps = ItemProps> = Omit<
    GalleryWithPaginatorProps<ItemPropsT>,
    'renderItemContent'
> & {
    /** Метод для рендера элемента */
    renderItemContent: (props: ItemPropsT, currentSlideIndex: number, shouldLoadSrc: boolean) => ReactNode;
};

/**
 * Обёртка над GalleryWithPaginator: до первого листания src только у текущего слайда и соседей,
 * после взаимодействия — у всех. Саму подстановку src и loading="lazy" делает потребитель.
 * См. третий аргумент метода renderItemContent (shouldLoadSrc), по нему нужно определять загружать ли изображение.
 *
 * @typeParam ItemPropsT - элемент слайда, обязан иметь id
 * @kind component
 */
export const GalleryWithPaginatorLazy = function GalleryWithPaginatorLazy<ItemPropsT extends ItemProps>({
    onSwipe,
    renderItemContent,
    ...props
}: GalleryWithPaginatorLazyProps<ItemPropsT>) {
    const [hasInteracted, setHasInteracted] = useState(false);

    const handleSwipe = useCallback(
        (slide: number) => {
            setHasInteracted(true);
            onSwipe?.(slide);
        },
        [onSwipe]
    );

    return (
        <GalleryWithPaginator
            {...props}
            onSwipe={handleSwipe}
            renderItemContent={(item, currentSlideIndex) =>
                renderItemContent(
                    item,
                    currentSlideIndex,
                    shouldLoadGalleryPaginatorSlide({
                        slideIndex: item.index,
                        currentSlideIndex,
                        hasInteracted,
                    })
                )
            }
        />
    );
};
